import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TreeNode } from 'primeng/api';
import { TreeNodeSelectEvent } from 'primeng/tree';
import _ from 'lodash';
import { CostCenter } from '../../../models/cost-center/cost-center';
import { CostCenterProxyService } from '../../../proxies/cost-center.proxy.service';
import { LocaleHelperService } from '../../../services/locale-helper.service';

/** What the picker shows for the current value before the tree loads, or in read-only mode. */
export interface CostCenterSelection {
  code: string;
  name: string;
}

/**
 * Reusable cost center picker (#5790, #5794): a TreeSelect over the whole cost-center tree (one call), where only
 * active nodes that accept charges can be picked — grouping nodes stay as the way to reach them, and a branch with
 * nothing pickable beneath it is dropped. Writes the cost center id into `control` and emits the cost center.
 */
@Component({
  selector: 'app-cost-center-select',
  templateUrl: './cost-center-select.component.html',
})
export class CostCenterSelectComponent implements OnInit {
  private readonly proxy = inject(CostCenterProxyService);
  private readonly locale = inject(LocaleHelperService);

  /** Holds the selected cost center id. */
  @Input({ required: true }) control!: FormControl<string | null>;
  /** The saved value's code and name, shown until the tree loads and in read-only mode. */
  @Input() current: CostCenterSelection | null = null;
  @Input() inputId?: string;
  @Input() placeholder = 'Select a cost center';
  @Input() readonly = false;

  @Output() costCenterChange = new EventEmitter<CostCenter | null>();

  readonly treeNodes = signal<TreeNode<CostCenter>[]>([]);
  readonly display = signal<string | null>(null);
  selectedValue: TreeNode<CostCenter> | null = null;

  ngOnInit(): void {
    this.display.set(this.current ? this.label(this.current.code, this.current.name) : null);
    if (this.readonly) return;
    this.proxy.getAll().subscribe((response) => {
      const centers = response.data ?? [];
      this.treeNodes.set(this.buildTree(centers));
      const selected = centers.find((c) => c.id === this.control.value);
      if (!_.isNil(selected)) {
        this.selectedValue = this.findNode(this.treeNodes(), selected.id);
        this.display.set(this.labelOf(selected));
      }
    });
  }

  onNodeSelect(event: TreeNodeSelectEvent): void {
    const center = (event.node as TreeNode<CostCenter>).data ?? null;
    if (_.isNil(center)) return;
    this.control.setValue(center.id);
    this.control.markAsDirty();
    this.display.set(this.labelOf(center));
    this.costCenterChange.emit(center);
  }

  onClear(): void {
    this.control.setValue(null);
    this.control.markAsDirty();
    this.display.set(null);
    this.costCenterChange.emit(null);
  }

  private labelOf(center: CostCenter): string {
    return this.label(center.code, this.locale.getLocalizedValue(center.name));
  }

  private label(code: string, name: string): string {
    return name ? `${code} · ${name}` : code;
  }

  /** Builds the tree from parentCode; inactive nodes are left out and dead-end branches pruned. */
  private buildTree(centers: CostCenter[]): TreeNode<CostCenter>[] {
    const active = centers.filter((c) => c.isActive !== false);
    const byParent = _.groupBy(active, (c) => c.parentCode || '');
    const codes = new Set(active.map((c) => c.code));

    const toNode = (center: CostCenter): TreeNode<CostCenter> | null => {
      const children = _.sortBy(byParent[center.code] ?? [], (c) => c.code)
        .map(toNode)
        .filter((n): n is TreeNode<CostCenter> => n !== null);
      const pickable = center.selectable !== false;
      if (!pickable && children.length === 0) return null;
      return {
        key: center.id,
        label: this.labelOf(center),
        data: center,
        selectable: pickable,
        children,
        leaf: children.length === 0,
        styleClass: pickable ? undefined : 'text-surface-500',
      };
    };

    // A node whose parent is missing (or inactive) is shown at the root rather than lost.
    const roots = active.filter((c) => !c.parentCode || !codes.has(c.parentCode));
    return _.sortBy(roots, (c) => c.code)
      .map(toNode)
      .filter((n): n is TreeNode<CostCenter> => n !== null);
  }

  private findNode(nodes: TreeNode<CostCenter>[], id: string): TreeNode<CostCenter> | null {
    for (const node of nodes) {
      if (node.key === id) return node;
      const found = this.findNode(node.children ?? [], id);
      if (found) return found;
    }
    return null;
  }
}
